import React from 'react';
import { useParams } from 'react-router-dom';
import { strParamReplacer, hasDeployedRevision } from 'Utils';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import useGlobalRules from 'Hooks/query-hooks/global-rules/useGlobalRules';
import useMerchantRules from 'Hooks/query-hooks/single-merchant/useMerchantRules';
import useGetParameters from 'Hooks/query-hooks/parameters/useGetParameters';
import useGetListNames from 'Hooks/query-hooks/list/useGetListNames';
import useDeployedMerchantRules from 'Hooks/query-hooks/single-merchant/useDeployedMerchantRules';

import errorController from 'Components/ErrorScreens';
import RouteLoading from 'Components/RouteLoading';
import appStyles from 'Routes/Routes.css';
import Header from './components/Header';
import MerchantOverview from './components/MerchantOverview';
import MerchantTable from './components/MerchantTable';

const MerchantOverrides = () => {
    const { mid } = useParams<{ mid: string }>();
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const listNamesAPI = useGetListNames();
    const parametersAPI = useGetParameters({
        queryParams: {
            status: JSON.stringify(['active'])
        }
    });
    const enableQuery =
        !listNamesAPI.isLoading && !listNamesAPI.isFetching && !parametersAPI.isLoading && !parametersAPI.isFetching;

    const globalRulesAPI = useGlobalRules({
        enable: enableQuery,
        parameterData: parametersAPI.data,
        listNameMap: listNamesAPI.data
    });
    const merchantRulesAPI = useMerchantRules({
        merchantId: mid,
        enabled: enableQuery,
        parameterData: parametersAPI.data,
        listNameMap: listNamesAPI.data
    });
    const deployedMerchantRulesAPI = useDeployedMerchantRules({
        merchantId: mid,
        enabled: enableQuery,
        listNameMap: listNamesAPI.data,
        parameterData: parametersAPI.data
    });

    if (
        merchantRulesAPI.isLoading ||
        merchantRulesAPI.isFetching ||
        globalRulesAPI.isLoading ||
        globalRulesAPI.isFetching
    ) {
        return <RouteLoading />;
    }

    const { hasError, errorCmp } = errorController([merchantRulesAPI.error, globalRulesAPI.error]);

    if (hasError) {
        return errorCmp;
    }

    const disableViewDeployed = !hasDeployedRevision(merchantRulesAPI.data!);

    const isViewDeployedVisible = !(
        deployedMerchantRulesAPI.isError ||
        deployedMerchantRulesAPI.isLoading ||
        deployedMerchantRulesAPI.isFetching
    );

    return (
        <div className={appStyles.landingPage}>
            <Header
                merchantId={mid}
                templateRepositoryId={templateRepositoryId!}
                disableLink={disableViewDeployed}
                isViewDeployedVisible={isViewDeployedVisible}
                canBeDeleted={!!merchantRulesAPI.data?.canBeDeleted}
                merchantEnabled={!!merchantRulesAPI.data?.config?.merchantEnabled}
            />
            <MerchantOverview
                merchantId={mid}
                templateRepositoryId={templateRepositoryId!}
                globalThreshold={Number(globalRulesAPI?.data?.config?.parameters?.overallThreshold)}
                globalThresholdText={strParamReplacer(globalRulesAPI.data?.config?.inputDescription, {
                    type: 'parameterMap',
                    parameterMap: globalRulesAPI.data?.config?.parameters!
                })}
                merchantRules={merchantRulesAPI.data as MerchantRulesData}
            />
            <MerchantTable merchantId={mid} globalRules={globalRulesAPI.data!} merchantRules={merchantRulesAPI.data!} />
        </div>
    );
};

export default React.memo(MerchantOverrides);
